import { type DefaultError, queryOptions } from '@tanstack/react-query';
import { t } from 'i18next';
import { useMutation } from '~/hooks/use-mutations';
import type { ApiError } from '~/lib/api';
import { toaster } from '~/modules/common/toaster';
import { addMenuItem, deleteMenuItem } from '~/modules/navigation/menu-sheet/helpers/menu-operations';
import {
  type CreateWorkspaceParams,
  type UpdateWorkspaceParams,
  createWorkspace,
  deleteWorkspaces,
  getWorkspace,
  updateWorkspace,
} from '~/modules/workspaces/api';
import type { Workspace } from '~/modules/workspaces/types';
import { useMutateQueryData } from '~/query/hooks/use-mutate-query-data';
import { queryClient } from '~/query/query-client';

// Keys for workspace query
export const workspacesKeys = {
  all: ['workspaces'] as const,
  single: {
    base: () => [...workspacesKeys.all, 'single'] as const,
    byIdOrSlug: (idOrSlug: string) => [...workspacesKeys.single.base(), idOrSlug] as const,
  },
  create: () => [...workspacesKeys.all, 'create'] as const,
  update: () => [...workspacesKeys.all, 'update'] as const,
  delete: () => [...workspacesKeys.all, 'delete'] as const,
};

// Query Options to get a workspace by id or slug and organization id or slug
export const workspaceQueryOptions = (idOrSlug: string, orgIdOrSlug: string) =>
  queryOptions({
    queryKey: workspacesKeys.single.byIdOrSlug(idOrSlug),
    queryFn: () => getWorkspace(idOrSlug, orgIdOrSlug),
  });
/**
 * Custom hook to create a new workspace.
 * This hook provides the functionality to create a new workspace.
 *
 * @returns The mutation hook for creating an workspace.
 */
export const useWorkspaceCreateMutation = () => {
  return useMutation<
    Workspace & {
      membership: NonNullable<Workspace['membership']>;
    },
    ApiError,
    CreateWorkspaceParams
  >({
    mutationKey: workspacesKeys.create(),
    mutationFn: createWorkspace,
    onSuccess: async (createdWorkspace) => {
      addMenuItem(createdWorkspace, 'workspaces');
      toaster(t('common:success.create_resource', { resource: t('app:workspace') }), 'success');
    },
  });
};

/**
 * Custom hook to update an existing workspace.
 * This hook provides the functionality to update an workspace. After a successful update,
 * it updates the local cache and invalidates relevant queries to keep the data fresh.
 *
 * @returns The mutation hook for updating an workspace.
 */
export const useUpdateWorkspaceMutation = () => {
  return useMutation<Workspace, DefaultError, UpdateWorkspaceParams>({
    mutationKey: workspacesKeys.update(),
    mutationFn: updateWorkspace,
    onSuccess(updatedWorkspace, { idOrSlug }) {
      toaster(t('common:success.update_resource', { resource: t('app:workspace') }), 'success');

      const mutateWorkspaceQueryCache = useMutateQueryData(workspacesKeys.single.byIdOrSlug(idOrSlug));
      mutateWorkspaceQueryCache.update([updatedWorkspace], 'workspace');
    },
    gcTime: 1000 * 10,
  });
};

/**
 * Custom hook to delete workspaces.
 * This hook provides the functionality to delete one or more workspaces.
 *
 * @returns The mutation hook for deleting workspaces.
 */
export const useWorkspaceDeleteMutation = () => {
  return useMutation<
    void,
    ApiError,
    {
      workspaces: Workspace[];
      orgIdOrSlug: string;
    }
  >({
    mutationKey: workspacesKeys.delete(),
    mutationFn: ({ orgIdOrSlug, workspaces }) => deleteWorkspaces({ ids: workspaces.map(({ id }) => id), orgIdOrSlug }),
    onSuccess: (_, { workspaces }) => {
      const workspaceIds = workspaces.map(({ id }) => id);

      toaster(t('common:success.delete_resources', { resources: t(`app:${workspaceIds.length > 1 ? 'workspaces' : 'workspace'}`) }), 'success');

      for (const id of workspaceIds) deleteMenuItem(id);

      queryClient.invalidateQueries({ queryKey: workspacesKeys.single.base() });
    },
  });
};
